import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Search,
  RefreshCw,
  Eye,
  Download,
  Clock,
  AlertCircle,
  Calendar,
  Euro,
  Building,
  User,
  Filter,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Signature,
  CheckCircle,
  XCircle,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { devisService } from '../services/devisService';

const Devis = () => {
  // États
  const [devis, setDevis] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("tous");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDevis, setSelectedDevis] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  // États pour la signature électronique
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [signatureCode, setSignatureCode] = useState("");
  const [devisToSign, setDevisToSign] = useState(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isSigningDevis, setIsSigningDevis] = useState(false);

  // États pour le refus de devis
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [devisToReject, setDevisToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isRejectingDevis, setIsRejectingDevis] = useState(false);

  const itemsPerPage = 10;

  // Animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  // Charger les devis au montage du composant
  useEffect(() => {
    loadDevis();
  }, []);

  const loadDevis = async () => {
    setIsLoading(true);
    try {
      const data = await devisService.getClientDevis(1, 100); // Charger tous les devis pour l'instant
      console.log('Devis chargés:', data);
      setDevis(data?.items || []);
    } catch (error) {
      console.error('Erreur lors du chargement des devis:', error);
      toast.error('Erreur lors du chargement des devis');
      setDevis([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonctions utilitaires
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      // Si la date est au format YYYYMMDD (ex: 20251010)
      if (typeof dateString === 'string' && dateString.length === 8 && /^\d{8}$/.test(dateString)) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        const date = new Date(year, month - 1, day); // month - 1 car les mois commencent à 0
        if (isNaN(date.getTime())) return "-";
        return format(date, "dd/MM/yyyy", { locale: fr });
      }
      // Sinon, traiter comme une date normale
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      return format(date, "dd/MM/yyyy", { locale: fr });
    } catch (error) {
      return "-";
    }
  };



  const formatCurrency = (amount) => {
    if (!amount) return "0,00 €";
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Déterminer le statut d'un devis
  const getDevisStatus = (devis) => {
    if (devis.signedByClient) return "valide";
    if (devis.status === "rejected") return "rejete";
    if (devis.expiryDate && new Date(devis.expiryDate) < new Date()) return "expire";
    return "en_attente";
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "valide": return "Validé";
      case "rejete": return "Rejeté";
      case "expire": return "Expiré";
      case "en_attente": return "Attente";
      default: return "Inconnu";
    }
  };

  // Calculer les statistiques
  const getStatistics = () => {
    if (!devis.length) {
      return {
        totalDevis: 0,
        devisEnAttente: 0,
        devisValides: 0,
        devisRejetes: 0,
        montantTotal: 0
      };
    }

    const devisEnAttente = devis.filter(d => getDevisStatus(d) === "en_attente").length;
    const devisValides = devis.filter(d => getDevisStatus(d) === "valide").length;
    const devisRejetes = devis.filter(d => getDevisStatus(d) === "rejete").length;
    const montantTotal = devis.reduce((sum, d) => {
      // Calculer le total des produits
      const total = d.products?.reduce((productSum, product) => {
        return productSum + (parseFloat(product.total) || 0);
      }, 0) || 0;
      return sum + total;
    }, 0);

    return {
      totalDevis: devis.length,
      devisEnAttente,
      devisValides,
      devisRejetes,
      montantTotal
    };
  };

  // Filtrer les devis
  const filteredDevis = devis.filter(devis => {
    const matchesSearch =
      devis.quotationNumber?.toString().includes(searchTerm.toLowerCase()) ||
      devis.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      devis.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      devis.contact?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "tous" || getDevisStatus(devis) === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredDevis.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDevis = filteredDevis.slice(startIndex, startIndex + itemsPerPage);

  // Fonctions pour les actions
  const handleViewDevis = async (devis) => {
    setPdfLoading(true);
    try {
      await devisService.viewDevis(devis);
      toast.success("PDF généré avec succès");
    } catch (error) {
      console.error("Erreur lors de la visualisation:", error);
      toast.error("Erreur lors de la visualisation du devis");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadDevis = async (devis) => {
    setPdfLoading(true);
    try {
      await devisService.downloadDevis(devis);
      toast.success("PDF téléchargé avec succès");
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      toast.error("Erreur lors du téléchargement du devis");
    } finally {
      setPdfLoading(false);
    }
  };

  // Fonction pour la signature électronique (comme les contrats)
  const handleSignDevis = async (devis) => {
    setIsGeneratingCode(true);
    try {
      // Générer le code de signature
      await devisService.generateSignatureCode([devis._id]);
      setDevisToSign(devis);
      setIsSignatureDialogOpen(true);
      toast.success("Code de signature envoyé par email");
    } catch (error) {
      console.error("Erreur lors de la génération du code:", error);
      toast.error("Erreur lors de la génération du code de signature");
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleSignatureSubmit = async () => {
    if (!signatureCode || signatureCode.length !== 6) {
      toast.error("Veuillez saisir un code de signature valide (6 chiffres)");
      return;
    }

    setIsSigningDevis(true);
    try {
      // Signer le devis avec le code (le nom sera récupéré automatiquement côté backend)
      await devisService.signQuotations([devisToSign._id], signatureCode);

      // Mettre à jour localement
      setDevis(prev => prev.map(d =>
        d._id === devisToSign._id
          ? { ...d, signedByClient: true, clientSignedDate: new Date().toISOString() }
          : d
      ));

      toast.success("Devis signé avec succès !");
      setIsSignatureDialogOpen(false);
      setSignatureCode("");
      setDevisToSign(null);
    } catch (error) {
      console.error("Erreur lors de la signature:", error);
      toast.error("Code de signature invalide ou erreur lors de la signature");
    } finally {
      setIsSigningDevis(false);
    }
  };

  // Fonction pour refuser un devis
  const handleRejectDevis = (devis) => {
    setDevisToReject(devis);
    setIsRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Veuillez saisir une raison pour le refus");
      return;
    }

    setIsRejectingDevis(true);
    try {
      // Appeler l'API pour refuser le devis
      await devisService.rejectQuotation(devisToReject._id, rejectionReason);

      // Mettre à jour localement
      setDevis(prev => prev.map(d =>
        d._id === devisToReject._id
          ? { ...d, status: 'rejected', rejectionReason, rejectedDate: new Date().toISOString() }
          : d
      ));

      toast.success("Devis refusé avec succès !");
      setIsRejectDialogOpen(false);
      setRejectionReason("");
      setDevisToReject(null);
    } catch (error) {
      console.error("Erreur lors du refus:", error);
      toast.error("Erreur lors du refus du devis");
    } finally {
      setIsRejectingDevis(false);
    }
  };



  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const data = await devisService.getClientDevis(1, 100);
      console.log('Devis actualisés:', data);
      setDevis(data?.items || []);
      toast.success("Devis actualisés");
    } catch (error) {
      console.error('Erreur lors de l\'actualisation:', error);
      toast.error('Erreur lors de l\'actualisation des devis');
      setDevis([]);
    } finally {
      setIsLoading(false);
    }
  };

  const statistics = getStatistics();

  return (
    <div className="min-h-screen  p-6">
      <motion.div
        className="max-w-7xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Indicateur de chargement PDF */}
        <AnimatePresence>
          {pdfLoading && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-3"
            >
              <Loader2 className="animate-spin text-blue-600" size={20} />
              <span className="text-blue-800 font-medium">Génération du PDF en cours...</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* En-tête */}
        <motion.div className="mb-8" variants={itemVariants}>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Mes Devis</h1>
          <p className="text-slate-600">Gérez vos devis et validez ceux qui vous conviennent</p>
        </motion.div>

        {/* Cartes de statistiques */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8" variants={itemVariants}>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <FileText size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-800">{statistics.totalDevis}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-xl">
                <Clock size={24} className="text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">En attente</p>
                <p className="text-2xl font-bold text-slate-800">{statistics.devisEnAttente}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-xl">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Validés</p>
                <p className="text-2xl font-bold text-slate-800">{statistics.devisValides}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-xl">
                <XCircle size={24} className="text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Rejetés</p>
                <p className="text-2xl font-bold text-slate-800">{statistics.devisRejetes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-xl">
                <Euro size={24} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Montant total</p>
                <p className="text-2xl font-bold text-slate-800">{formatCurrency(statistics.montantTotal)}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filtres et recherche */}
        <motion.div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6" variants={itemVariants}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Barre de recherche */}
            <div className="flex items-center space-x-4">
              <div className="relative flex-1 lg:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher un devis..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filtres et actions */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter size={20} className="text-slate-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="tous">Tous les statuts</option>
                  <option value="en_attente">En attente</option>
                  <option value="valide">Validés</option>
                  <option value="rejete">Rejetés</option>
                  <option value="expire">Expirés</option>
                </select>
              </div>

              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
                <span>Actualiser</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Liste des devis */}
        <motion.div className="bg-white rounded-xl shadow-sm border border-slate-200" variants={itemVariants}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-slate-600">Chargement des devis...</span>
            </div>
          ) : filteredDevis.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">Aucun devis trouvé</h3>
              <p className="text-slate-500">
                {searchTerm || statusFilter !== "tous"
                  ? "Aucun devis ne correspond à vos critères de recherche."
                  : "Vous n'avez pas encore de devis."
                }
              </p>
            </div>
          ) : (
            <div>
              {/* En-tête des colonnes */}
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                <div className="grid grid-cols-12 gap-2 items-center text-sm font-medium text-slate-600">
                  <div className="col-span-2">Devis</div>
                  <div className="col-span-2">Client</div>
                  <div className="col-span-2">Dates</div>
                  <div className="col-span-1">Montant</div>
                  <div className="col-span-2">Signataire</div>
                  <div className="col-span-1">Statut</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
              </div>

              {/* Liste des devis */}
              <div className="divide-y divide-slate-200">
                <AnimatePresence>
                  {paginatedDevis.map((devis, index) => (
                    <motion.div
                      key={devis._id}
                      className="p-6 hover:bg-slate-50 transition-colors"
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      transition={{ delay: index * 0.05 }}
                    >
                    {/* Grille avec colonnes fixes et bien alignées */}
                    <div className="grid grid-cols-12 gap-2 items-center w-full">
                      {/* Numéro et description - 3 colonnes */}
                      <div className="col-span-2 flex items-center space-x-3 min-w-0">
                        <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                          <FileText size={20} className="text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-800 truncate"> #{devis.quotationNumber || devis._id?.slice(-6)}</p>
                          <p className="text-sm text-slate-500 truncate">{devis.comment || "Aucun commentaire"}</p>
                        </div>
                      </div>

                      {/* Client - 2 colonnes */}
                      <div className="col-span-2 flex items-center space-x-3 min-w-0">
                        <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                          <Building size={20} className="text-purple-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-800 truncate">{devis.client?.name || "Client"}</p>
                          <p className="text-sm text-slate-500 truncate">{devis.contact || "Contact non disponible"}</p>
                        </div>
                      </div>

                      {/* Dates - 2 colonnes */}
                      <div className="col-span-2 flex items-center space-x-3 min-w-0">
                        <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                          <Calendar size={20} className="text-indigo-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-800 text-sm">{formatDate(devis.createdAt)}</p>
                          <p className="text-xs text-slate-500">Expire: {formatDate(devis.expiryDate)}</p>
                        </div>
                      </div>

                      {/* Montant - 1.5 colonnes */}
                      <div className="col-span-1 flex items-center space-x-2 min-w-0">
                        <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                          <Euro size={18} className="text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-800 text-sm">
                            {formatCurrency(devis.products?.reduce((sum, product) => sum + (parseFloat(product.total) || 0), 0) || 0)}
                          </p>
                          <p className="text-xs text-slate-500">HT</p>
                        </div>
                      </div>

                      {/* Signataire - 2 colonnes */}
                      <div className="col-span-2 flex items-center space-x-3 min-w-0">
                        <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                          <User size={18} className="text-amber-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          {devis.signedByClient ? (
                            <>
                              <p className="font-semibold text-slate-800 text-sm truncate">
                                {devis.signingName || "Client"}
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatDate(devis.clientSignedDate)}
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-slate-500">Non signé</p>
                              <p className="text-xs text-slate-400">En attente</p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Statut - 1.5 colonnes */}
                      <div className="col-span-1 flex items-center space-x-2 min-w-0">
                        <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                          {getDevisStatus(devis) === "valide" ? (
                            <CheckCircle size={18} className="text-green-600" />
                          ) : getDevisStatus(devis) === "rejete" ? (
                            <XCircle size={18} className="text-red-600" />
                          ) : getDevisStatus(devis) === "expire" ? (
                            <AlertCircle size={18} className="text-gray-600" />
                          ) : (
                            <Clock size={18} className="text-orange-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            getDevisStatus(devis) === "valide"
                              ? "bg-green-100 text-green-800"
                              : getDevisStatus(devis) === "rejete"
                              ? "bg-red-100 text-red-800"
                              : getDevisStatus(devis) === "expire"
                              ? "bg-gray-100 text-gray-800"
                              : "bg-orange-100 text-orange-800"
                          }`}>
                            {getStatusLabel(getDevisStatus(devis))}
                          </div>
                        </div>
                      </div>

                      {/* Actions - 1 colonne */}
                      <div className="col-span-2 flex items-center justify-end space-x-1">
                        <button
                          onClick={() => handleViewDevis(devis)}
                          disabled={pdfLoading}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Voir le devis"
                        >
                          {pdfLoading ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-600 border-t-transparent"></div>
                          ) : (
                            <Eye size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDownloadDevis(devis)}
                          disabled={pdfLoading}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Télécharger le devis"
                        >
                          {pdfLoading ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-green-600 border-t-transparent"></div>
                          ) : (
                            <Download size={16} />
                          )}
                        </button>
                        {!devis.signedByClient && getDevisStatus(devis) === "en_attente" && (
                          <>
                            <button
                              onClick={() => handleSignDevis(devis)}
                              disabled={isGeneratingCode}
                              className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Signer le devis"
                            >
                              {isGeneratingCode ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-orange-600 border-t-transparent"></div>
                              ) : (
                                <Signature size={16} />
                              )}
                            </button>
                            <button
                              onClick={() => handleRejectDevis(devis)}
                              disabled={isRejectingDevis}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Refuser le devis"
                            >
                              {isRejectingDevis ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-600 border-t-transparent"></div>
                              ) : (
                                <X size={16} />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredDevis.length)} sur {filteredDevis.length} devis
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? "bg-blue-500 text-white"
                            : "text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>



        {/* Modal de signature électronique */}
        {isSignatureDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Signature size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Signature électronique</h3>
                  <p className="text-sm text-slate-600">Devis {devisToSign?.quotationNumber}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-slate-700 mb-4">
                  Pour signer ce devis, veuillez saisir le code de signature à 6 chiffres qui vous a été envoyé par email.
                </p>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Code de signature
                  </label>
                  <input
                    type="text"
                    value={signatureCode}
                    onChange={(e) => setSignatureCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono tracking-widest"
                    maxLength={6}
                  />
                  <p className="text-xs text-slate-500">
                    Saisissez les 6 chiffres reçus par email
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setIsSignatureDialogOpen(false);
                    setSignatureCode("");
                    setDevisToSign(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSignatureSubmit}
                  disabled={signatureCode.length !== 6 || isSigningDevis}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {isSigningDevis ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Signature en cours...</span>
                    </>
                  ) : (
                    <span>Signer le devis</span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Modal de refus de devis */}
        {isRejectDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <X size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Refuser le devis</h3>
                  <p className="text-sm text-slate-600">Devis #{devisToReject?.quotationNumber}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-slate-700 mb-4">
                  Veuillez indiquer la raison du refus de ce devis.
                </p>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Raison du refus
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Expliquez pourquoi vous refusez ce devis..."
                    rows={4}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setIsRejectDialogOpen(false);
                    setRejectionReason("");
                    setDevisToReject(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleRejectSubmit}
                  disabled={!rejectionReason.trim() || isRejectingDevis}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {isRejectingDevis ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Refus en cours...</span>
                    </>
                  ) : (
                    <span>Refuser le devis</span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Devis;
